import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Editor } from 'primereact/editor';
import { Fieldset } from 'primereact/fieldset';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabMenu } from 'primereact/tabmenu';
import { useEffect, useMemo, useState } from 'react';

import { useSite } from '../../context/site';
import ProgramTypeDropdown from '../program-type-dropdown';
import StateDropdown from '../state-dropdown';
import { GetEditorFormats } from '../../shared';

function ProgramUpdate({ open, data, onClose }) {
    const { UpdateProgram } = useSite();

    const [activeIndex, setActiveIndex] = useState(0);

    const [active, setActive] = useState(true);
    const [sponsorJ2TC, setSponsorJ2TC] = useState(false);
    const [programType, setProgramType] = useState('');
    const [programName, setProgramName] = useState('');
    const [contactNameFirst, setContactNameFirst] = useState('');
    const [contactNameLast, setContactNameLast] = useState('');
    const [contactTitle, setContactTitle] = useState('');
    const [email, setEmail] = useState('');
    const [fax, setFax] = useState('');
    const [siteStreetAddress1, setSiteStreetAddress1] = useState('');
    const [siteStreetAddress2, setSiteStreetAddress2] = useState('');
    const [siteCity, setSiteCity] = useState('');
    const [siteState, setSiteState] = useState('');
    const [siteZipCode, setSiteZipCode] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneExt, setPhoneExt] = useState('');
    const [notes, setNotes] = useState('');
    const [mailingStreetAddress1, setMailingStreetAddress1] = useState('');
    const [mailingStreetAddress2, setMailingStreetAddress2] = useState('');
    const [mailingCity, setMailingCity] = useState('');
    const [mailingState, setMailingState] = useState('');
    const [mailingZipCode, setMailingZipCode] = useState('');
    const [website, setWebsite] = useState('');

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const tabItems = [{ label: 'Basic' }, { label: 'Site Address' }, { label: 'Mailing Address' }];

    const buttonDisabled = () => {
        if (active !== data.active) {
            return false;
        }

        if (sponsorJ2TC !== data.sponsorJ2TC) {
            return false;
        }

        if (programType !== data.programType._id) {
            return false;
        }

        if (programName !== data.programName) {
            return false;
        }

        if (contactNameFirst !== data.contactNameFirst) {
            return false;
        }

        if (contactNameLast !== data.contactNameLast) {
            return false;
        }

        if (contactTitle !== data.contactTitle) {
            return false;
        }

        if (email !== data.email) {
            return false;
        }

        if (fax !== data.fax) {
            if (!fax || fax.length < 10) {
                return true;
            }

            return false;
        }

        if (phone !== data.phone) {
            if (!phone || phone.length < 10) {
                return true;
            }

            return false;
        }

        if (phoneExt !== data.phoneExt) {
            return false;
        }

        if (website !== data.website) {
            return false;
        }

        if (notes !== data.notes) {
            return false;
        }

        if (siteStreetAddress1 !== data.siteStreetAddress1) {
            return false;
        }

        if (siteStreetAddress2 !== data.siteStreetAddress2) {
            return false;
        }

        if (siteCity !== data.siteCity) {
            return false;
        }

        if (siteState !== data.siteState._id) {
            return false;
        }

        if (siteZipCode !== data.siteZipCode) {
            if (!siteZipCode || siteZipCode.length < 5) {
                return true;
            }

            return false;
        }

        if (mailingStreetAddress1 !== data.mailingStreetAddress1) {
            return false;
        }

        if (mailingStreetAddress2 !== data.mailingStreetAddress2) {
            return false;
        }

        if (mailingCity !== data.mailingCity) {
            return false;
        }

        if (mailingState !== data.mailingState._id) {
            return false;
        }

        if (mailingZipCode !== data.mailingZipCode) {
            if (!mailingZipCode || mailingZipCode.length < 5) {
                return true;
            }

            return false;
        }

        return true;
    };

    const onTabChange = ({ index }) => {
        setActiveIndex(index);
    };

    const reset = () => {
        setActiveIndex(0);

        setActive(true);
        setSponsorJ2TC(false);
        setProgramType('');
        setProgramName('');
        setContactNameFirst('');
        setContactNameLast('');
        setContactTitle('');
        setEmail('');
        setFax('');
        setPhone('');
        setPhoneExt('');
        setWebsite('');
        setNotes('');

        setSiteStreetAddress1('');
        setSiteStreetAddress2('');
        setSiteCity('');
        setSiteState('');
        setSiteZipCode('');

        setMailingStreetAddress1('');
        setMailingStreetAddress2('');
        setMailingCity('');
        setMailingState('');
        setMailingZipCode('');

        setProcessing(false);
        setProcessingError('');
    };

    const onCancel = () => {
        reset();
        onClose({ status: 499, message: 'Canceled' });
    };

    const onUpdate = () => {
        const updateData = { _id: data._id };

        if (active !== data.active) {
            updateData.active = active;
        }

        if (sponsorJ2TC !== data.sponsorJ2TC) {
            updateData.sponsorJ2TC = sponsorJ2TC;
        }

        if (programType !== data.programType) {
            updateData.programType = programType;
        }

        if (programName !== data.programName) {
            updateData.programName = programName;
        }

        if (contactNameFirst !== data.contactNameFirst) {
            updateData.contactNameFirst = contactNameFirst;
        }

        if (contactNameLast !== data.contactNameLast) {
            updateData.contactNameLast = contactNameLast;
        }

        if (contactTitle !== data.contactTitle) {
            updateData.contactTitle = contactTitle;
        }

        if (email !== data.email) {
            updateData.email = email;
        }

        if (fax !== data.fax) {
            updateData.fax = fax;
        }

        if (phone !== data.phone) {
            updateData.phone = phone;
        }

        if (phoneExt !== data.phoneExt) {
            updateData.phoneExt = phoneExt;
        }

        if (website !== data.website) {
            updateData.website = website;
        }

        if (siteStreetAddress1 !== data.siteStreetAddress1) {
            updateData.siteStreetAddress1 = siteStreetAddress1;
        }

        if (siteStreetAddress2 !== data.siteStreetAddress2) {
            updateData.siteStreetAddress2 = siteStreetAddress2;
        }

        if (siteCity !== data.siteCity) {
            updateData.siteCity = siteCity;
        }

        if (siteState !== data.siteState) {
            updateData.siteState = siteState;
        }

        if (siteZipCode !== data.siteZipCode) {
            updateData.siteZipCode = siteZipCode;
        }

        if (mailingStreetAddress1 !== data.mailingStreetAddress1) {
            updateData.mailingStreetAddress1 = mailingStreetAddress1;
        }

        if (mailingStreetAddress2 !== data.mailingStreetAddress2) {
            updateData.mailingStreetAddress2 = mailingStreetAddress2;
        }

        if (mailingCity !== data.mailingCity) {
            updateData.mailingCity = mailingCity;
        }

        if (mailingState !== data.mailingState) {
            updateData.mailingState = mailingState;
        }

        if (mailingZipCode !== data.mailingZipCode) {
            updateData.mailingZipCode = mailingZipCode;
        }

        if (notes !== data.notes) {
            updateData.notes = notes;
        }

        UpdateProgram(updateData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                reset();
                onClose(res);
            }
        });
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Update"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onUpdate}
            />
        </div>
    );

    // On load set values
    useEffect(() => {
        setActive(data.active);
        setSponsorJ2TC(data.sponsorJ2TC);
        setProgramType(data.programType._id);
        setProgramName(data.programName);
        setContactNameFirst(data.contactNameFirst);
        setContactNameLast(data.contactNameLast);
        setContactTitle(data.contactTitle);
        setEmail(data.email);
        setFax(data.fax);
        setPhone(data.phone);
        setPhoneExt(data.phoneExt);
        setWebsite(data.website);
        setNotes(data.notes);

        setSiteStreetAddress1(data.siteStreetAddress1);
        setSiteStreetAddress2(data.siteStreetAddress2);
        setSiteCity(data.siteCity);
        setSiteState(data.siteState._id);
        setSiteZipCode(data.siteZipCode);

        setMailingStreetAddress1(data.mailingStreetAddress1);
        setMailingStreetAddress2(data.mailingStreetAddress2);
        setMailingCity(data.mailingCity);
        setMailingState(data.mailingState._id);
        setMailingZipCode(data.mailingZipCode);
    }, [data]);

    return (
        <Dialog
            header="Update Program"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <TabMenu model={tabItems} activeIndex={activeIndex} onTabChange={onTabChange} />

            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            {activeIndex !== 0 || processing ? null : (
                <TabBasic
                    active={active}
                    setActive={setActive}
                    sponsorJ2TC={sponsorJ2TC}
                    setSponsorJ2TC={setSponsorJ2TC}
                    programType={programType}
                    setProgramType={setProgramType}
                    programName={programName}
                    setProgramName={setProgramName}
                    contactNameFirst={contactNameFirst}
                    setContactNameFirst={setContactNameFirst}
                    contactNameLast={contactNameLast}
                    setContactNameLast={setContactNameLast}
                    contactTitle={contactTitle}
                    setContactTitle={setContactTitle}
                    email={email}
                    setEmail={setEmail}
                    fax={fax}
                    setFax={setFax}
                    phone={phone}
                    setPhone={setPhone}
                    phoneExt={phoneExt}
                    setPhoneExt={setPhoneExt}
                    website={website}
                    setWebsite={setWebsite}
                    notes={notes}
                    setNotes={setNotes}
                />
            )}

            {activeIndex !== 1 || processing ? null : (
                <TabSiteAddress
                    siteStreetAddress1={siteStreetAddress1}
                    setSiteStreetAddress1={setSiteStreetAddress1}
                    siteStreetAddress2={siteStreetAddress2}
                    setSiteStreetAddress2={setSiteStreetAddress2}
                    siteCity={siteCity}
                    setSiteCity={setSiteCity}
                    siteState={siteState}
                    setSiteState={setSiteState}
                    siteZipCode={siteZipCode}
                    setSiteZipCode={setSiteZipCode}
                />
            )}

            {activeIndex !== 2 || processing ? null : (
                <TabMailingAddress
                    mailingStreetAddress1={mailingStreetAddress1}
                    setMailingStreetAddress1={setMailingStreetAddress1}
                    mailingStreetAddress2={mailingStreetAddress2}
                    setMailingStreetAddress2={setMailingStreetAddress2}
                    mailingCity={mailingCity}
                    setMailingCity={setMailingCity}
                    mailingState={mailingState}
                    setMailingState={setMailingState}
                    mailingZipCode={mailingZipCode}
                    setMailingZipCode={setMailingZipCode}
                />
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Updating program...</p>

            <ProgressSpinner />
        </>
    );
}

function TabBasic({
    active,
    setActive,
    sponsorJ2TC,
    setSponsorJ2TC,
    programType,
    setProgramType,
    programName,
    setProgramName,
    contactNameFirst,
    setContactNameFirst,
    contactNameLast,
    setContactNameLast,
    contactTitle,
    setContactTitle,
    email,
    setEmail,
    fax,
    setFax,
    phone,
    setPhone,
    phoneExt,
    setPhoneExt,
    website,
    setWebsite,
    notes,
    setNotes
}) {
    const getPhoneClassname = useMemo(() => {
        const count = phone.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [phone]);

    const getFaxClassname = useMemo(() => {
        const count = fax.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [fax]);

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="activeId" className="font-bold block mb-2">
                        Active
                    </label>

                    <InputSwitch
                        inputId="activeId"
                        checked={active}
                        onChange={(e) => {
                            setActive(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="sponsorJ2TCrId" className="font-bold block mb-2">
                        Sponsor J2TC
                    </label>

                    <InputSwitch
                        inputId="sponsorJ2TCId"
                        checked={sponsorJ2TC}
                        onChange={(e) => {
                            setSponsorJ2TC(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="programNameId" className="font-bold block mb-2">
                        Program Name
                    </label>

                    <InputText
                        id="programNameId"
                        value={programName}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={48}
                        onChange={(e) => {
                            setProgramName(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="programTypeId" className="font-bold block mb-2">
                        Program Type
                    </label>

                    <ProgramTypeDropdown
                        programTypeId={programType}
                        onChange={(programTypeId) => {
                            setProgramType(programTypeId);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="contactNameFirstId" className="font-bold block mb-2">
                        Contact First Name
                    </label>

                    <InputText
                        id="contactNameFirstId"
                        value={contactNameFirst}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setContactNameFirst(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="contactNameLastId" className="font-bold block mb-2">
                        Contact Last Name
                    </label>

                    <InputText
                        id="contactNameLastId"
                        value={contactNameLast}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setContactNameLast(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="contactTitleId" className="font-bold block mb-2">
                        Contact Title
                    </label>

                    <InputText
                        id="contactTitleId"
                        value={contactTitle}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setContactTitle(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="emailId" className="font-bold block mb-2">
                        Email
                    </label>

                    <InputText
                        id="emailId"
                        value={email}
                        maxLength={48}
                        onChange={(e) => {
                            setEmail(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="phoneId" className="font-bold block mb-2">
                        Phone
                    </label>

                    <InputText
                        id="phoneId"
                        value={phone}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getPhoneClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setPhone(e.target.value);
                        }}
                    />

                    {phone.length === 0 || phone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>

                <div className="flex-auto">
                    <label htmlFor="phoneExtId" className="font-bold block mb-2">
                        Phone Ext
                    </label>

                    <InputText
                        id="phoneExtId"
                        value={phoneExt}
                        maxLength={6}
                        keyfilter={/^[0-9]*$/}
                        onChange={(e) => {
                            setPhoneExt(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="faxId" className="font-bold block mb-2">
                        Fax
                    </label>

                    <InputText
                        id="faxId"
                        value={fax}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getFaxClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setFax(e.target.value);
                        }}
                    />

                    {fax.length === 0 || fax.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The fax number should be exactly 10 digits
                        </small>
                    )}
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="websiteId" className="font-bold block mb-2">
                        Website
                    </label>

                    <InputText
                        id="websiteId"
                        value={website}
                        maxLength={42}
                        onChange={(e) => {
                            setWebsite(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="notesId" className="font-bold block mb-2">
                        Notes
                    </label>

                    <Editor
                        id="notesId"
                        value={notes}
                        headerTemplate={<GetEditorFormats />}
                        onTextChange={(e) => setNotes(e.htmlValue)}
                        style={{ height: '320px' }}
                    />
                </div>
            </div>
        </div>
    );
}

function TabSiteAddress({
    siteStreetAddress1,
    setSiteStreetAddress1,
    siteStreetAddress2,
    setSiteStreetAddress2,
    siteCity,
    setSiteCity,
    siteState,
    setSiteState,
    siteZipCode,
    setSiteZipCode
}) {
    return (
        <div className="mt-2">
            <Fieldset legend="Site">
                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="streetAddress1Id" className="font-bold block mb-2">
                            Address Line 1
                        </label>

                        <InputText
                            id="streetAddress1Id"
                            value={siteStreetAddress1}
                            keyfilter={/^[a-zA-Z0-9 ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setSiteStreetAddress1(e.target.value);
                            }}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="streetAddress2Id" className="font-bold block mb-2">
                            Address Line 2
                        </label>

                        <InputText
                            id="streetAddress2Id"
                            value={siteStreetAddress2}
                            keyfilter={/^[a-zA-Z0-9 ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setSiteStreetAddress2(e.target.value);
                            }}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="cityId" className="font-bold block mb-2">
                            City
                        </label>

                        <InputText
                            id="cityId"
                            value={siteCity}
                            keyfilter={/^[a-zA-Z ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setSiteCity(e.target.value);
                            }}
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="stateId" className="font-bold block mb-2">
                            State
                        </label>

                        <StateDropdown
                            stateId={siteState}
                            onChange={(stateId) => {
                                setSiteState(stateId);
                            }}
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="zipId" className="font-bold block mb-2">
                            Zip Code
                        </label>

                        <InputText
                            id="zipId"
                            value={siteZipCode}
                            keyfilter={/^[0-9]*$/}
                            minLength={5}
                            maxLength={5}
                            onChange={(e) => {
                                setSiteZipCode(e.target.value);
                            }}
                        />
                    </div>
                </div>
            </Fieldset>
        </div>
    );
}

function TabMailingAddress({
    mailingStreetAddress1,
    setMailingStreetAddress1,
    mailingStreetAddress2,
    setMailingStreetAddress2,
    mailingCity,
    setMailingCity,
    mailingState,
    setMailingState,
    mailingZipCode,
    setMailingZipCode
}) {
    return (
        <div className="mt-2">
            <Fieldset legend="Mailing">
                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="streetAddress1Id" className="font-bold block mb-2">
                            Address Line 1
                        </label>

                        <InputText
                            id="streetAddress1Id"
                            value={mailingStreetAddress1}
                            keyfilter={/^[a-zA-Z0-9 ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setMailingStreetAddress1(e.target.value);
                            }}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="streetAddress2Id" className="font-bold block mb-2">
                            Address Line 2
                        </label>

                        <InputText
                            id="streetAddress2Id"
                            value={mailingStreetAddress2}
                            keyfilter={/^[a-zA-Z0-9 ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setMailingStreetAddress2(e.target.value);
                            }}
                        />
                    </div>
                </div>

                <div className="card flex flex-wrap gap-3 p-fluid">
                    <div className="flex-auto">
                        <label htmlFor="cityId" className="font-bold block mb-2">
                            City
                        </label>

                        <InputText
                            id="cityId"
                            value={mailingCity}
                            keyfilter={/^[a-zA-Z ]*$/}
                            maxLength={42}
                            onChange={(e) => {
                                setMailingCity(e.target.value);
                            }}
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="stateId" className="font-bold block mb-2">
                            State
                        </label>

                        <StateDropdown
                            stateId={mailingState}
                            onChange={(stateId) => {
                                setMailingState(stateId);
                            }}
                        />
                    </div>

                    <div className="flex-auto">
                        <label htmlFor="zipId" className="font-bold block mb-2">
                            Zip Code
                        </label>

                        <InputText
                            id="zipId"
                            value={mailingZipCode}
                            keyfilter={/^[0-9]*$/}
                            minLength={5}
                            maxLength={5}
                            onChange={(e) => {
                                setMailingZipCode(e.target.value);
                            }}
                        />
                    </div>
                </div>
            </Fieldset>
        </div>
    );
}

export default ProgramUpdate;
